/**
 * Browser feature probe.
 *
 * Per BLIND_SPOTS.md §1.9 / PROJECT_STATE task #25: ReadyRoomOps
 * commits to Safari 15.6+, iOS 15.6+, and the last two stable
 * versions of Chrome/Firefox/Edge (see `browserslist` in package.json).
 *
 * The compile-time target (Vite `build.target`) controls SYNTAX; this
 * module probes RUNTIME APIs that are required by the cleaner flow.
 * If any required feature is missing, `main.tsx` renders the
 * unsupported-browser page instead of mounting React — avoiding the
 * white screen of death on a too-old device.
 *
 * Required-features rationale (each one is on the cleaner critical
 * path; degrading gracefully is not possible):
 *   - crypto.randomUUID    — idempotency keys for every mutation
 *   - fetch                — every API + Storage call
 *   - File                 — photo upload input
 *   - createImageBitmap    — processImage HEIC → JPEG normalisation
 *   - Canvas2D             — processImage resize + EXIF rotation
 *   - localStorage         — auth session persistence
 *   - Promise.allSettled   — used in offline-queue replay
 */

export interface FeatureProbeResult {
  ok:      boolean
  missing: readonly string[]
}

interface ProbeEntry {
  name:  string
  check: () => boolean
}

const REQUIRED_FEATURES: readonly ProbeEntry[] = [
  {
    name:  'crypto.randomUUID',
    check: () => typeof globalThis.crypto?.randomUUID === 'function',
  },
  {
    name:  'fetch',
    check: () => typeof globalThis.fetch === 'function',
  },
  {
    name:  'File',
    check: () => typeof globalThis.File === 'function',
  },
  {
    name:  'createImageBitmap',
    check: () => typeof globalThis.createImageBitmap === 'function',
  },
  {
    name:  'Canvas2D',
    check: () => {
      try {
        const c = document.createElement('canvas')
        return Boolean(c.getContext?.('2d'))
      } catch { return false }
    },
  },
  {
    name:  'localStorage',
    check: () => {
      // Safari Private Browsing throws on access; treat as missing.
      try { return typeof globalThis.localStorage === 'object' && globalThis.localStorage !== null }
      catch { return false }
    },
  },
  {
    name:  'Promise.allSettled',
    check: () => typeof Promise.allSettled === 'function',
  },
]

export function probeRequiredFeatures(): FeatureProbeResult {
  const missing: string[] = []
  for (const { name, check } of REQUIRED_FEATURES) {
    if (!check()) missing.push(name)
  }
  return { ok: missing.length === 0, missing }
}

export const REQUIRED_FEATURE_NAMES: readonly string[] =
  REQUIRED_FEATURES.map((f) => f.name)
